<template>
	<div>
		<div class="form-container">
			<div class="form-input col-2">
				<label> Annual Review Year </label>
				<select v-model="selectedAnnualReviewId" @change="onSelectedYearChangeAsync">
					<option v-for="option in yearOptions" :key="option.id" :value="option.id">
						{{ option.name }}
					</option>
				</select>
			</div>
		</div>
		<div v-if="isLoading" class="spinner-container">
			<Spinner />
		</div>
		<div v-else-if="selectedAnnualReviewId && annualReviewDetails">
			<AnnualReviewDetailsComponent
				:loanId="loanId"
				:lenderId="lenderId"
				:productTypeId="productTypeId"
				:programTypeId="programTypeId"
				:referenceId="referenceId"
				:applicationUrl="applicationUrl"
				:annualReviewDetails="annualReviewDetails"
				:selectedAnnualReviewId="selectedAnnualReviewId"
				@reloadAnnualReviewAsync="fetchAnnualReviewDataAsync" />
			<CourtesyRepullDetails
				v-if="annualReviewDetails.hasCourtesyRepull"
				:loanId="loanId"
				:lenderId="lenderId"
				:productTypeId="productTypeId"
				:programTypeId="programTypeId"
				:referenceId="referenceId"
				:applicationUrl="applicationUrl"
				:annualReviewDetails="annualReviewDetails"
				:selectedAnnualReviewId="selectedAnnualReviewId"
				@reloadAnnualReviewAsync="fetchAnnualReviewDataAsync" />
		</div>
	</div>
</template>

<script setup lang="ts">
	import { ref, computed, onMounted } from 'vue';
	import { useRoute, useRouter } from 'vue-router';
	import axios from 'axios';
	import functions from '@/use/functions';
	import { DateOption, AnnualReview, AnnualReviewDetails } from '@/models/loans';
	import Spinner from '@/components/shared/Spinner.vue';
	import AnnualReviewDetailsComponent from '@/components/loan/annual-review/AnnualReviewDetails.vue';
	import CourtesyRepullDetails from '@/components/loan/annual-review/CourtesyRepullDetails.vue';

	const props = defineProps<{
		loanId: number;
		lenderId: number;
		productTypeId: number;
		programTypeId: number;
		referenceId: string;
		applicationUrl: string;
		annualReviews: AnnualReview[];
	}>();

	const route = useRoute();
	const router = useRouter();

	const isLoading = ref<boolean>(false);
	const selectedAnnualReviewId = ref<number>();
	const annualReviewDetails = ref<AnnualReviewDetails | null>();

	const yearOptions = computed<DateOption[]>(() => {
		return props.annualReviews.map(
			(review): DateOption => ({
				id: review.id,
				name: review.annualReviewYear.toString()
			})
		);
	});

	const selectInitialAnnualReview = () => {
		const queryAnnualReviewId = Number(route.query.annualReviewId);

		if (isNaN(queryAnnualReviewId) || !props.annualReviews.some((review) => review.id === queryAnnualReviewId)) {
			selectedAnnualReviewId.value = yearOptions.value[0].id;
		} else {
			selectedAnnualReviewId.value = queryAnnualReviewId;
		}
	};

	const onSelectedYearChangeAsync = async () => {
		if (!selectedAnnualReviewId.value) {
			return;
		}

		router.replace({
			query: { ...route.query, annualReviewId: selectedAnnualReviewId.value },
			hash: route.hash
		});

		await fetchAnnualReviewDataAsync();
	};

	const fetchAnnualReviewDataAsync = async () => {
		isLoading.value = true;
		annualReviewDetails.value = null;

		try {
			const response = await axios(`/api/annualreview/${selectedAnnualReviewId.value}`);

			annualReviewDetails.value = response.data;
		} catch (error) {
			const review = props.annualReviews.find((r) => r.id === selectedAnnualReviewId.value);
			const reviewYear = review ? review.annualReviewYear : 'Unknown';

			functions.openModal({
				title: 'Oops!',
				description: `There was an issue getting the data for annual review year ${reviewYear}. Please try again later.`
			});
		} finally {
			isLoading.value = false;
		}
	};

	onMounted(async () => {
		selectInitialAnnualReview();

		await fetchAnnualReviewDataAsync();
	});
</script>

<style scoped src="@/assets/css/loans/form.css" />

<style scoped>
	.spinner-container {
		display: flex;
		justify-content: center;
		margin-top: 4rem;
	}

	.form-container {
		margin-bottom: 1rem;
	}

	select {
		height: fit-content;
		padding: 8px;
	}

	button {
		width: 100%;
		max-height: 38px;
		padding: 8px;
		background-color: black;
		color: white;
		border: none;
		border-radius: 4px;
		cursor: pointer;
		font-size: 13px;
		font-family: 'Montserrat', arial, sans-serif;
		font-weight: bold;
		transition: background-color 0.2s, color 0.2s, opacity 0.2s;
	}

	button:hover {
		background-color: #333;
	}

	button:disabled {
		background-color: #ddd;
		color: #666;
		cursor: not-allowed;
		opacity: 0.7;
	}
</style>
